import React, {
  FC,
  createContext,
  useContext,
  useRef,
  useState,
} from 'react';
import { isEqual, cloneDeep } from 'lodash';
import { EventEmitter } from './useEventEmitter';

interface SimpleStateEvent {
  type: string;
  keys: string[];
  preState: any;
  newState: any;
}

export class SimpleState<T> {
  private readonly state: T = null;

  constructor(iniState: T) {
    this.state = iniState;
  }

  public event: EventEmitter<SimpleStateEvent> = new EventEmitter();

  public update = (updater: (state: T) => void, skip: boolean) => {
    const preState: any = cloneDeep(this.state);
    updater(this.state);
    if (skip) {
      return;
    }
    const newState = this.getReadState();
    this.event.emit('onChange', {
      preState,
      newState,
      type: 'onChange',
      keys: [],
    });
  };

  public getReadState: () => T = () => new Proxy(this.state as any, {
    get(target: T, p: PropertyKey): any {
      return (target as any)[p];
    },
    set(): boolean {
      return true;
    },
  });
}

export function getSimpleContext<S>(iniState?: S) {
  const Context = createContext<SimpleState<S>>({} as any);
  const Provider: FC<{ value: any }> = function (props) {
    // const state = useRef(new SimpleState<S>({ ...(iniState || {}), ...(props.value || {}) }));
    const state = useRef(new SimpleState<S>({ ...iniState, ...props.value }));
    return <Context.Provider value={state.current}>{props.children}</Context.Provider>;
  };
  const useSimpleContext: (getKeys?: (state: S) => any[]) => {
    state: S;
    update: (updater: (state: S) => void, skip?: boolean) => void;
  } = (getKeys?: Function) => {
    const ctx = useContext(Context);
    const [_, setState] = useState(0);
    try {
      ctx.event.useSubscription('onChange', ({ newState, preState }) => {
        if (!getKeys) {
          return;
        }
        const prev: any[] = getKeys(preState);
        const next: any[] = getKeys(newState);

        for (let i = 0; i < prev.length; i++) {
          if (!isEqual(prev[i], next[i])) {
            setState((pv) => pv + 1);
            break;
          }
        }
      });
      return { state: ctx.getReadState(), update: ctx.update };
    } catch (error) {
      console.error(error);
      return { state: {}, update: () => { } };
    }
  };
  return { Provider, useSimpleContext };
}
